/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Rating, Tab, Typography } from '@mui/material';
import { DatePickers, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, CompanyPeopleFilterDTO, DemandDTO, DemandFilterDTO, PeopleDTO, PeopleFilterDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDate, formatDateTime } from '../../../shared/utils';
import { TabContext, TabList, TabPanel } from '@mui/lab';

interface IProjectMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const ProjectMaintenance: React.FC<IProjectMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [categoryList, setCategoryList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [priorityList, setPriorityList] = useState<ValueListDTO[]>([]);
    const [responsibleList, setResposibleList] = useState<PeopleDTO[]>([]);
    const [requesterList, setRequesterList] = useState<UserDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [demandDTO, setDemandDTO] = useState<DemandDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    const [value, setValue] = React.useState('1');

    useEffect(() => {
        setDemandDTO({
            ...demandDTO,
            id: '',
            name: '',
            code: 'PRJ',
            externalCode: '',
            typeId: '',
            categoryId: '',
            statusId: ListId.worksphere.demand.startStatus,
            description: '',
            escope: '',
            outEscope: '',
            premises: '',
            restrictions: '',
            priorityId: '',
            kanbanIndex: 0,
            startDateTime: undefined,
            expectedClosingDateTime: undefined,
            estimatedEffort: 0,
            responsibleId: '',
            requesterId: '',
            companyId: userInfo.user.companyId,
        } as DemandDTO);

        if (isOpenMaintenance) {
            handleListGetAll(ListId.worksphere.demand.categoryId, setCategoryList);
            handleListGetAll(ListId.worksphere.demand.typeId, setTypeList);
            handleListGetAll(ListId.worksphere.demand.statusId, setStatusList);
            handleListGetAll(ListId.worksphere.demand.priorityId, setPriorityList);
            handleCompanyGetAll();
            handleUserGetAllAsync();

            handleGetDemandByIdAsync();

            handleGetPermission(ListId.worksphere.demandId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        handleCompanyPeopleGetAllAsync(demandDTO?.companyId || '', setRequesterList);
        handleCompanyPeopleGetAllAsync(demandDTO?.companyId || '', setResposibleList);
    }, [demandDTO?.companyId]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'valueindex, value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleCompanyPeopleGetAllAsync = useCallback(async (companyId: string, setPeopleList: any) => {
        if (companyId === undefined || companyId === '') {
            return;
        }
        const companyPeopleFilterDTO: CompanyPeopleFilterDTO = {
            searchAssociated: true,
            companyId: companyId
        };
        GenericService.GetAllViewAsync1('corpsphere.companypeople', companyPeopleFilterDTO, setActionExecuted, setLoading, setPeopleList, showMessage, false);

    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const userFilterDTO = {
            companyId: userInfo.user.CompanyId,
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetDemandByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('worksphere.demand', data.id, setLoading, setDemandDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = demandDTO as DemandDTO;

        if (demandDTO?.id === '' || demandDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            dto.code = companyList.find(x => x.id === dto.companyId)?.code;

            GenericService.AddAsync1('worksphere.demand', dto, setDemandDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('worksphere.demand', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [demandDTO]);

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='PROJECT DETAILS'
                icon='grid_view_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <TabContext value={value} >
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <TabList onChange={handleChange} aria-label="lab API tabs example">
                                        <Tab label="Details" value="1" />
                                        <Tab label="Scope" value="2" />
                                        <Tab label="Out Scope" value="3" />
                                        <Tab label="Premises" value="4" />
                                        <Tab label="Restrictions" value="5" />
                                    </TabList>
                                </Box>
                                <TabPanel value="1" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <SelectField
                                                id='companyId'
                                                label='Company'
                                                placeholder='Company'
                                                value={demandDTO?.companyId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={companyList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextFieldInput
                                                id='name'
                                                label='Name'
                                                value={demandDTO?.name}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={true}
                                                maxLength={200}
                                                autoFocus={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='code'
                                                label='Code'
                                                value={demandDTO?.code}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={true}
                                                maxLength={20}
                                                readonly={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <SelectField
                                                id='requesterId'
                                                label='Requester'
                                                placeholder='Owner'
                                                value={demandDTO?.requesterId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={requesterList}
                                                required={true}
                                                hasNoneSelect={false}
                                                labelKey='fullName'
                                                valueKey='peopleId'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <SelectField
                                                id='responsibleId'
                                                label='Responsible'
                                                placeholder='Responsible'
                                                value={demandDTO?.responsibleId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={responsibleList}
                                                required={true}
                                                hasNoneSelect={false}
                                                labelKey='fullName'
                                                valueKey='peopleId'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <SelectField
                                                id='statusId'
                                                label='Status'
                                                placeholder='Status'
                                                value={demandDTO?.statusId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={statusList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='externalCode'
                                                label='External Code'
                                                value={demandDTO?.externalCode}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={true}
                                                maxLength={200}
                                                autoFocus={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='estimatedEffort'
                                                label='Estimated Effort hrs'
                                                value={demandDTO?.estimatedEffort}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={true}
                                                maxLength={200}
                                                autoFocus={false}
                                                type='number'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='startDateTime'
                                                label={'Start Date'}
                                                value={demandDTO?.startDateTime}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={false}
                                                type='datetime-local'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='expectedClosingDateTime'
                                                label={'Expected Closing Date'}
                                                value={demandDTO?.expectedClosingDateTime}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={false}
                                                type='datetime-local'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <SelectField
                                                id='priorityId'
                                                label='Priority'
                                                placeholder='Priority'
                                                value={demandDTO?.priorityId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={priorityList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <SelectField
                                                id='typeId'
                                                label='Type'
                                                placeholder='Type'
                                                value={demandDTO?.typeId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={typeList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <SelectField
                                                id='categoryId'
                                                label='Category'
                                                placeholder='Category'
                                                value={demandDTO?.categoryId}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                valueList={categoryList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='description'
                                                label='Description'
                                                value={demandDTO?.description}
                                                setValue={setDemandDTO}
                                                obj={demandDTO}
                                                required={true}
                                                maxLength={400}
                                                autoFocus={false}
                                                multiline={true}
                                                rows={4}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="2" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid item xs={12} md={12}>
                                        <TextFieldInput
                                            id='scope'
                                            label='Scope'
                                            value={demandDTO?.scope}
                                            setValue={setDemandDTO}
                                            obj={demandDTO}
                                            required={true}
                                            maxLength={400}
                                            autoFocus={false}
                                            multiline={true}
                                            rows={14}
                                        />
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="3" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid item xs={12} md={12}>
                                        <TextFieldInput
                                            id='outScope'
                                            label='Out Scope'
                                            value={demandDTO?.outScope}
                                            setValue={setDemandDTO}
                                            obj={demandDTO}
                                            required={true}
                                            maxLength={400}
                                            autoFocus={false}
                                            multiline={true}
                                            rows={14}
                                        />
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="4" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid item xs={12} md={12}>
                                        <TextFieldInput
                                            id='premises'
                                            label='Premises'
                                            value={demandDTO?.premises}
                                            setValue={setDemandDTO}
                                            obj={demandDTO}
                                            required={true}
                                            maxLength={400}
                                            autoFocus={false}
                                            multiline={true}
                                            rows={14}
                                        />
                                    </Grid>
                                </TabPanel>
                                <TabPanel value='5' sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid item xs={12} md={12}>
                                        <TextFieldInput
                                            id='restrictions'
                                            label='Restrictions'
                                            value={demandDTO?.restrictions}
                                            setValue={setDemandDTO}
                                            obj={demandDTO}
                                            required={true}
                                            maxLength={400}
                                            autoFocus={false}
                                            multiline={true}
                                            rows={14}
                                        />
                                    </Grid>
                                </TabPanel>
                            </TabContext>
                        </Grid>
                        {demandDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={demandDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === demandDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(demandDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === demandDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(demandDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};